/** @jsxImportSource @emotion/react */
import { useDraggable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { formatDate, photoTagsPresenter } from '@kaiizen/kai-presenters';
import {
  usePhotoUriForLayoutSize,
  useScanEditingContextSlice,
  useUsers,
} from '@kaiizen/kai-pro-api-react-state';
import {
  scanPartHasDuplicatePhotos,
  type ScanPhoto,
} from '@kaiizen/kai-scan-template-system';

import { useLazyInView } from '@/Client/Lib';
import { FocusPanelTitlebar, type ScanFocusPanelPage } from '@/Client/Partials/FocusPanel';
import { useNavigateToPhoto } from '@/Client/State';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { PhotoDropZone } from '@/Client/Widgets/PhotoDropZone';
import { GalleryMediaTile, GallerySectionHeader } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { GalleryEmptyState } from './GalleryEmptyState';
import {
  GalleryPhotoManagement,
  type GalleryPhotoManagementContract,
} from './GalleryPhotoManagement';
import {
  type GalleryPhotoOccurrence,
  getGalleryPhotoOccurrenceKey,
} from './galleryPhotoOperations';
import type {
  FocusedGalleryGroup,
  FocusedGalleryModel,
  FocusedGallerySection,
  GalleryScopeTarget,
} from './gallerySections';
import { GalleryTaskHeader } from './GalleryTaskHeader';
import { useGalleryPhotoUpload } from './useGalleryPhotoUpload';
import { useGalleryScopeNavigation } from './useGalleryScopeNavigation';

const WORKSPACE_STYLE = css`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CONTENT_STYLE = css`
  min-width: 0;
`;

const GROUP_SHEET_STYLE = css`
  margin-bottom: 22px;
`;

const GRID_STYLE = css`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 7px;
  min-width: 0;
  margin-bottom: 2px;
`;

const GROUP_HEADER_STYLE = {
  position: 'sticky',
  top: -1,
  zIndex: 6,
  marginBottom: 0,
  padding: '9px 0',
} as const;

const HEADER_ACTION_STYLE = css`
  min-height: 24px;
  padding: 3px 8px;
  border: 1px solid ${styleguideColors.stroke};
  border-radius: 0;
  background: transparent;
  color: ${styleguideColors.textSecondary};
  font-family: "Maxeville Construct", Maxeville, monospace;
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: ${styleguideColors.ink};
    background: ${styleguideColors.white};
    color: ${styleguideColors.ink};
  }
`;

type GalleryUsersById = ReturnType<typeof useUsers>['usersById'];

const GALLERY_SCOPE_LABELS = {
  overview: 'Overview',
  group: 'Group',
  task: 'Task',
} as const;

const getGalleryPhotoTagsLabel = (tags: readonly string[] | undefined): string | undefined =>
  tags ? [...new Set(tags.map(photoTagsPresenter).filter(Boolean))].join(', ') || undefined : undefined;

const getGalleryPhotoSourceLabel = (photo: ScanPhoto): string | undefined =>
  [
    photo.photoSource === 'camera'
      ? 'Captured'
      : photo.photoSource === 'upload'
      ? 'Uploaded'
      : undefined,
    photo.addedAt ? formatDate(photo.addedAt) : undefined,
    getGalleryPhotoTagsLabel(photo.tags),
  ].filter(Boolean).join(' · ') || undefined;

const getGalleryPhotoOccurrence = (
  photo: ScanPhoto,
  section: FocusedGallerySection,
): GalleryPhotoOccurrence => ({
  key: getGalleryPhotoOccurrenceKey({
    ...section.scanPartContext,
    photoId: photo.photoId,
  }),
  photo,
  context: section.scanPartContext,
  sectionTitle: section.title,
});

const getGallerySectionOccurrences = (
  section: FocusedGallerySection,
): GalleryPhotoOccurrence[] => section.photos.map(photo => getGalleryPhotoOccurrence(photo, section));

const getGalleryPhotoSiblings = (section: FocusedGallerySection) => {
  const photoCounts = new Map<string, number>();
  return section.photos.map(photo => {
    const photoIndex = photoCounts.get(photo.photoId) ?? 0;
    photoCounts.set(photo.photoId, photoIndex + 1);
    return { key: JSON.stringify([photo.photoId, photoIndex]), photo };
  });
};

/** Returns visible assignments, collapsing only identical assignment keys. */
const getVisibleGalleryOccurrences = (
  model: FocusedGalleryModel,
  collapsedGroupIds: ReadonlySet<string>,
): GalleryPhotoOccurrence[] => [...new Map(
  [
    ...(model.overview ? [model.overview] : []),
    ...model.groups.flatMap(group => (
      collapsedGroupIds.has(group.id) ? [] : [group, ...group.tasks]
    )),
  ].flatMap(getGallerySectionOccurrences).map(occurrence => [occurrence.key, occurrence]),
).values()];

const getVisibleGalleryOccurrenceKeySignature = (
  visibleOccurrences: readonly GalleryPhotoOccurrence[],
): string => JSON.stringify(visibleOccurrences.map(occurrence => occurrence.key));

type GallerySelection = {
  routeIdentity: string;
  selectedOccurrenceKeys: ReadonlySet<string>;
};

const reconcileSelectedGalleryOccurrenceKeys = (
  selectedOccurrenceKeys: ReadonlySet<string>,
  visibleOccurrenceKeySignature: string,
): ReadonlySet<string> => {
  const visibleOccurrenceKeys = new Set(JSON.parse(visibleOccurrenceKeySignature) as string[]);
  if ([...selectedOccurrenceKeys].every(key => visibleOccurrenceKeys.has(key))) {
    return selectedOccurrenceKeys;
  }
  return new Set([...selectedOccurrenceKeys].filter(key => visibleOccurrenceKeys.has(key)));
};

const getSelectedGalleryOccurrences = (
  selectedOccurrenceKeys: ReadonlySet<string>,
  visibleOccurrencesByKey: ReadonlyMap<string, GalleryPhotoOccurrence>,
): Map<string, GalleryPhotoOccurrence> =>
  new Map(
    [...selectedOccurrenceKeys].flatMap(key => {
      const occurrence = visibleOccurrencesByKey.get(key);
      return occurrence ? [[key, occurrence]] : [];
    }),
  );

const toggleGalleryOccurrenceKey = (
  selectedOccurrenceKeys: ReadonlySet<string>,
  occurrenceKey: string,
): ReadonlySet<string> => {
  const nextSelectedOccurrenceKeys = new Set(selectedOccurrenceKeys);
  if (nextSelectedOccurrenceKeys.has(occurrenceKey)) nextSelectedOccurrenceKeys.delete(occurrenceKey);
  else nextSelectedOccurrenceKeys.add(occurrenceKey);
  return nextSelectedOccurrenceKeys;
};

const GalleryTile = ({
  photo,
  section,
  index,
  scanIsEditable,
  usersById,
  selectedOccurrences,
  onToggleSelection,
}: {
  photo: ScanPhoto;
  section: FocusedGallerySection;
  index: number;
  scanIsEditable: boolean;
  usersById: GalleryUsersById;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  onToggleSelection: (occurrence: GalleryPhotoOccurrence) => void;
}) => {
  // Seven 4:3 tiles across the focus panel: about 150px wide by default, about 200px with the navigator narrowed.
  const imageUrl = usePhotoUriForLayoutSize(photo.photoId, 200);
  const navigateToPhoto = useNavigateToPhoto();
  const visibilityRef = useRef<HTMLDivElement>(null);
  const shouldLoad = useLazyInView(visibilityRef);
  const label = `${section.title} photo ${index + 1}`;
  const occurrence = getGalleryPhotoOccurrence(photo, section);
  /** Dragging a tile that's part of the active multi-selection carries the whole
   *  selection along; otherwise it's just this one photo. */
  const isPartOfSelection = selectedOccurrences.size > 1 && selectedOccurrences.has(occurrence.key);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `gallery-tile-${occurrence.key}`,
    data: {
      type: 'photo',
      uri: imageUrl,
      sources: isPartOfSelection ? [...selectedOccurrences.values()] : [occurrence],
    },
  });
  const setNode = (node: HTMLDivElement | null) => {
    visibilityRef.current = node;
    setNodeRef(node);
  };

  return (
    <div ref={setNode} {...attributes} {...listeners} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <GalleryMediaTile
        model={{
          id: photo.photoId,
          label,
          imageUrl: shouldLoad ? imageUrl : undefined,
          scopeLabel: GALLERY_SCOPE_LABELS[section.kind],
          actorLabel: photo.addedByUserId ? usersById?.[photo.addedByUserId]?.name : undefined,
          sourceLabel: getGalleryPhotoSourceLabel(photo),
          selected: selectedOccurrences.has(occurrence.key),
        }}
        showSelectionControl={scanIsEditable}
        onToggleSelection={() => onToggleSelection(occurrence)}
        onOpen={() =>
          navigateToPhoto({
            photoId: photo.photoId,
            groupId: section.scanPartContext.groupId,
            taskId: section.scanPartContext.taskId,
          })}
      />
    </div>
  );
};

const SectionActions = ({
  section,
  onAdd,
  onRemoveDuplicates,
}: {
  section: FocusedGallerySection;
  onAdd: () => void;
  onRemoveDuplicates?: (section: FocusedGallerySection) => void | Promise<void>;
}) => (
  <>
    <button
      type='button'
      aria-label={`Add photos to ${section.title}`}
      css={HEADER_ACTION_STYLE}
      onClick={onAdd}
    >
      + Add
    </button>
    {onRemoveDuplicates && scanPartHasDuplicatePhotos(section.scanPart)
      ? (
        <button type='button' css={HEADER_ACTION_STYLE} onClick={() => onRemoveDuplicates(section)}>
          Remove duplicates
        </button>
      )
      : null}
  </>
);

const GallerySection = ({
  section,
  meta,
  task = false,
  collapsed = false,
  scanIsEditable,
  usersById,
  selectedOccurrences,
  onToggleSelection,
  onTitleSelect,
  onToggle,
  onRemoveDuplicates,
}: {
  section: FocusedGallerySection;
  meta?: string;
  task?: boolean;
  collapsed?: boolean;
  scanIsEditable: boolean;
  usersById: GalleryUsersById;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  onToggleSelection: (occurrence: GalleryPhotoOccurrence) => void;
  onTitleSelect?: () => void;
  onToggle?: () => void;
  onRemoveDuplicates?: (section: FocusedGallerySection) => void | Promise<void>;
}) => {
  const { inputRef, openFilePicker, onSelectFiles } = useGalleryPhotoUpload(section);

  const fileInput = scanIsEditable
    ? (
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        multiple
        hidden
        onChange={onSelectFiles}
      />
    )
    : null;

  const actions = scanIsEditable
    ? (
      <SectionActions
        section={section}
        onAdd={openFilePicker}
        onRemoveDuplicates={onRemoveDuplicates}
      />
    )
    : undefined;

  return (
    <PhotoDropZone
      scanPart={scanIsEditable ? section.scanPart : null}
      scanPartContext={section.scanPartContext}
      label={`Drop photos into ${section.title}`}
    >
      <div
        data-focused-gallery-section={section.kind}
        data-gallery-landmark={section.kind}
        data-gallery-overview={section.kind === 'overview' ? 'true' : undefined}
        data-gallery-group-id={section.scanPartContext.groupId}
        data-gallery-task-id={section.scanPartContext.taskId}
        style={{ scrollMarginTop: task ? 40 : 0 }}
      >
        {task
          ? (
            <GalleryTaskHeader
              title={section.title}
              photoCount={section.photos.length}
              onTitleSelect={onTitleSelect}
              actions={actions}
            />
          )
          : (
            <GallerySectionHeader
              title={section.title}
              meta={meta ?? `${section.photos.length} ${section.photos.length === 1 ? 'photo' : 'photos'}`}
              collapsed={collapsed}
              onTitleSelect={onTitleSelect}
              onToggle={onToggle}
              actions={actions}
              css={GROUP_HEADER_STYLE}
            />
          )}
        {fileInput}
        {!collapsed && section.photos.length
          ? (
            <div css={GRID_STYLE} data-gallery-photo-grid='true'>
              {getGalleryPhotoSiblings(section).map(({ key, photo }, index) => (
                <GalleryTile
                  key={key}
                  photo={photo}
                  section={section}
                  index={index}
                  scanIsEditable={scanIsEditable}
                  usersById={usersById}
                  selectedOccurrences={selectedOccurrences}
                  onToggleSelection={onToggleSelection}
                />
              ))}
            </div>
          )
          : null}
      </div>
    </PhotoDropZone>
  );
};

const GalleryGroup = ({
  group,
  scanIsEditable,
  usersById,
  selectedOccurrences,
  onToggleSelection,
  collapsed,
  onSelectGroup,
  onSelectTask,
  onToggle,
  onRemoveDuplicates,
}: {
  group: FocusedGalleryGroup;
  collapsed: boolean;
  scanIsEditable: boolean;
  usersById: GalleryUsersById;
  selectedOccurrences: ReadonlyMap<string, GalleryPhotoOccurrence>;
  onToggleSelection: (occurrence: GalleryPhotoOccurrence) => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onToggle?: () => void;
  onRemoveDuplicates?: (section: FocusedGallerySection) => void | Promise<void>;
}) => (
  <div css={GROUP_SHEET_STYLE}>
    <GallerySection
      section={group}
      meta={`${group.tasks.length} ${group.tasks.length === 1 ? 'task' : 'tasks'} · ${group.photoCount} ${group.photoCount === 1 ? 'photo' : 'photos'}`}
      collapsed={collapsed}
      scanIsEditable={scanIsEditable}
      usersById={usersById}
      selectedOccurrences={selectedOccurrences}
      onToggleSelection={onToggleSelection}
      onTitleSelect={onSelectGroup ? () => onSelectGroup(group.id) : undefined}
      onToggle={onToggle}
      onRemoveDuplicates={onRemoveDuplicates}
    />
    {!collapsed
      ? group.tasks.map(task => (
        <GallerySection
          key={task.id}
          section={task}
          task
          scanIsEditable={scanIsEditable}
          usersById={usersById}
          selectedOccurrences={selectedOccurrences}
          onToggleSelection={onToggleSelection}
          onTitleSelect={onSelectTask ? () => onSelectTask(task.id) : undefined}
          onRemoveDuplicates={onRemoveDuplicates}
        />
      ))
      : null}
  </div>
);

export const FocusedPhotoGallery = ({
  model,
  activePanel = 'photos',
  onSelectPanel = () => undefined,
  onSelectAll,
  onSelectGroup,
  onSelectTask,
  onActiveScopeChange,
  onRemoveDuplicates,
  management,
  walkthroughUploadEnabled = false,
  onUploadWalkthrough,
}: {
  model: FocusedGalleryModel;
  activePanel?: ScanFocusPanelPage;
  onSelectPanel?: (panel: ScanFocusPanelPage) => void;
  onSelectAll?: () => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onActiveScopeChange?: (target: GalleryScopeTarget) => void;
  onRemoveDuplicates?: (section: FocusedGallerySection) => void | Promise<void>;
  management?: GalleryPhotoManagementContract;
  walkthroughUploadEnabled?: boolean;
  onUploadWalkthrough?: () => void;
}) => {
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  const { usersById } = useUsers();
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(new Set());
  // Selecting a task opens its group for good, if it was collapsed. Adjusted while rendering the new selection (not in
  // an effect, which would also run on mount and could undo a Collapse clicked before it ran).
  const selectedTargetKey = `${model.selectedGroupId ?? ''}/${model.selectedTaskId ?? ''}`;
  const [previousSelectedTargetKey, setPreviousSelectedTargetKey] = useState(selectedTargetKey);
  if (selectedTargetKey !== previousSelectedTargetKey) {
    setPreviousSelectedTargetKey(selectedTargetKey);
    if (model.selectedTaskId && model.selectedGroupId && collapsedGroupIds.has(model.selectedGroupId)) {
      setCollapsedGroupIds(new Set([...collapsedGroupIds].filter(groupId => groupId !== model.selectedGroupId)));
    }
  }
  const selectedGroup = model.groups.find(group => group.id === model.selectedGroupId);
  const selectedTask = selectedGroup?.tasks.find(task => task.id === model.selectedTaskId);
  const effectiveCollapsedGroupIds = model.selectedTaskId && model.selectedGroupId
    ? new Set([...collapsedGroupIds].filter(groupId => groupId !== model.selectedGroupId))
    : collapsedGroupIds;
  const routeIdentity = 'gallery';
  const visibleOccurrences = getVisibleGalleryOccurrences(model, effectiveCollapsedGroupIds);
  const visibleOccurrencesByKey = new Map(visibleOccurrences.map(occurrence => [occurrence.key, occurrence]));
  const visibleOccurrenceKeySignature = getVisibleGalleryOccurrenceKeySignature(visibleOccurrences);
  const landmarkSignature = JSON.stringify([
    model.groups.map(group => [group.id, group.tasks.map(task => task.id)]),
    [...effectiveCollapsedGroupIds],
  ]);
  const contentRef = useGalleryScopeNavigation({
    selectedTarget: {
      groupId: model.selectedGroupId,
      taskId: model.selectedTaskId,
    },
    landmarkSignature,
    onActiveTargetChange: onActiveScopeChange,
  });
  const [selection, setSelection] = useState<GallerySelection>(() => ({
    routeIdentity,
    selectedOccurrenceKeys: new Set(),
  }));
  const selectedOccurrences = scanIsEditable && selection.routeIdentity === routeIdentity
    ? getSelectedGalleryOccurrences(selection.selectedOccurrenceKeys, visibleOccurrencesByKey)
    : new Map<string, GalleryPhotoOccurrence>();
  const clearSelection = () =>
    setSelection({
      routeIdentity,
      selectedOccurrenceKeys: new Set(),
    });
  const selectAllVisible = () =>
    setSelection({
      routeIdentity,
      selectedOccurrenceKeys: new Set(visibleOccurrencesByKey.keys()),
    });
  const toggleSelection = (occurrence: GalleryPhotoOccurrence) =>
    setSelection(selected => ({
      routeIdentity,
      selectedOccurrenceKeys: toggleGalleryOccurrenceKey(
        selected.routeIdentity === routeIdentity ? selected.selectedOccurrenceKeys : new Set(),
        occurrence.key,
      ),
    }));
  const toggleGroup = (groupId: string) => {
    const collapsing = !collapsedGroupIds.has(groupId);
    if (collapsing && model.selectedTaskId && model.selectedGroupId === groupId) {
      onSelectGroup?.(groupId);
    }
    setCollapsedGroupIds(collapsedGroupIds => {
      const nextCollapsedGroupIds = new Set(collapsedGroupIds);
      if (nextCollapsedGroupIds.has(groupId)) nextCollapsedGroupIds.delete(groupId);
      else nextCollapsedGroupIds.add(groupId);
      return nextCollapsedGroupIds;
    });
  };

  useEffect(() =>
    setSelection(selected => {
      if (!scanIsEditable || selected.routeIdentity !== routeIdentity) {
        return { routeIdentity, selectedOccurrenceKeys: new Set() };
      }
      const selectedOccurrenceKeys = reconcileSelectedGalleryOccurrenceKeys(
        selected.selectedOccurrenceKeys,
        visibleOccurrenceKeySignature,
      );
      return selectedOccurrenceKeys === selected.selectedOccurrenceKeys
        ? selected
        : { routeIdentity, selectedOccurrenceKeys };
    }), [routeIdentity, scanIsEditable, visibleOccurrenceKeySignature]);
  useEffect(() => {
    if (!selectedOccurrences.size) return;
    const clearSelectionOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelection({
          routeIdentity,
          selectedOccurrenceKeys: new Set(),
        });
      }
    };
    window.addEventListener('keydown', clearSelectionOnEscape);
    return () => window.removeEventListener('keydown', clearSelectionOnEscape);
  }, [routeIdentity, selectedOccurrences.size]);

  return (
    <div css={WORKSPACE_STYLE} data-focused-photo-gallery='true'>
      <FocusPanelTitlebar
        activePanel={activePanel}
        onSelectPanel={onSelectPanel}
        scopeLevel={model.level}
        groupTitle={selectedGroup?.title}
        taskTitle={selectedTask?.title}
        selectedGroupId={selectedGroup?.id}
        onSelectAll={onSelectAll}
        onSelectGroup={onSelectGroup}
      >
        {model.isPhotoEmpty
          ? (
            <GalleryEmptyState
              overview={model.overview}
              groups={model.emptyUploadGroups}
              scanIsEditable={scanIsEditable}
              walkthroughUploadEnabled={walkthroughUploadEnabled}
              onUploadWalkthrough={onUploadWalkthrough}
            />
          )
          : (
            <div ref={contentRef} css={CONTENT_STYLE} data-gallery-content='true'>
              {model.overview
                ? (
                  <div css={GROUP_SHEET_STYLE}>
                    <GallerySection
                      section={model.overview}
                      meta={model.overview.photos.length === 1 ? 'Cover photo' : undefined}
                      scanIsEditable={scanIsEditable}
                      usersById={usersById}
                      selectedOccurrences={selectedOccurrences}
                      onToggleSelection={toggleSelection}
                      onRemoveDuplicates={onRemoveDuplicates}
                    />
                  </div>
                )
                : null}
              {model.groups.map(group => {
                const collapsible = true;
                return (
                  <GalleryGroup
                    key={group.id}
                    group={group}
                    collapsed={collapsible && effectiveCollapsedGroupIds.has(group.id)}
                    scanIsEditable={scanIsEditable}
                    usersById={usersById}
                    selectedOccurrences={selectedOccurrences}
                    onToggleSelection={toggleSelection}
                    onSelectGroup={onSelectGroup}
                    onSelectTask={onSelectTask}
                    onToggle={collapsible ? () => toggleGroup(group.id) : undefined}
                    onRemoveDuplicates={onRemoveDuplicates}
                  />
                );
              })}
            </div>
          )}
      </FocusPanelTitlebar>
      {selectedOccurrences.size
        ? (
          <GalleryPhotoManagement
            management={management}
            selectedOccurrences={selectedOccurrences}
            clearSelection={clearSelection}
            selectAllVisible={selectAllVisible}
            scanIsEditable={scanIsEditable}
          />
        )
        : null}
    </div>
  );
};
